/**
 * Operadores em risco — ocupação, atraso e SLA aparecem como motivos separados.
 */
import React, { useMemo } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import type { TeamMemberWorkload } from '../../../modules/minha-mesa/types/workItem';
import { MAX_ACTIVE_TASKS_PER_OPERATOR } from '../benchmark/teamCapacityShared';
import { EXEC_HERO_CLASS, EXEC_SECTION_LABEL, getOccupancyTier } from './opsExecutiveShared';
import {
  classifyOperatorRisk,
  compareOperatorRisk,
  operatorRiskBadges,
} from './opsTeamMetrics';

interface OpsOperatorsAtRiskBlockProps {
  members: TeamMemberWorkload[];
  onSelectMember?: (userId: string) => void;
}

function badgeClass(badge: string): string {
  if (badge === 'SOBRECARGA') return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
  if (badge === 'ATENÇÃO DE CARGA') return 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
  if (badge === 'RISCO DE SLA') return 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-200';
}

export const OpsOperatorsAtRiskBlock: React.FC<OpsOperatorsAtRiskBlockProps> = ({
  members,
  onSelectMember,
}) => {
  const atRisk = useMemo(() => {
    return members
      .map((member) => {
        const occupationPct = Math.round((member.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
        const reasons = classifyOperatorRisk({
          occupationPct,
          tasksOverdue: member.tasks_overdue,
          slaViolatedPct: member.sla_violated_pct,
        });
        const badges = operatorRiskBadges({
          occupationPct,
          tasksOverdue: member.tasks_overdue,
          slaViolatedPct: member.sla_violated_pct,
        });
        return { ...member, occupationPct, reasons, badges };
      })
      .filter((member) => member.reasons.length > 0)
      .sort((a, b) => compareOperatorRisk(
        { occupationPct: a.occupationPct, tasksOverdue: a.tasks_overdue, slaViolatedPct: a.sla_violated_pct, userId: a.user_id },
        { occupationPct: b.occupationPct, tasksOverdue: b.tasks_overdue, slaViolatedPct: b.sla_violated_pct, userId: b.user_id },
      ))
      .slice(0, 5);
  }, [members]);

  if (atRisk.length === 0) {
    return (
      <div className={`${EXEC_HERO_CLASS} border-l-[3px] border-l-emerald-500/70 px-5 py-3`}>
        <p className={EXEC_SECTION_LABEL}>Top Operadores em Risco</p>
        <p className="mt-1 text-[13px] font-medium text-emerald-600">Equipe dentro da capacidade operacional.</p>
      </div>
    );
  }

  return (
    <div className={`${EXEC_HERO_CLASS} shrink-0 border-l-[3px] border-l-amber-500/80`}>
      <div className="flex items-center gap-2 border-b border-[var(--border-default)]/50 px-5 py-2">
        <AlertTriangle size={16} className="text-amber-600" />
        <span className={EXEC_SECTION_LABEL}>Top Operadores em Risco</span>
        <span className="ml-auto text-[9px] text-[var(--text-muted)]">Motivo separado de ocupação, atraso e SLA</span>
      </div>
      <ul className="divide-y divide-[var(--border-default)]/40">
        {atRisk.map((member, index) => (
          <li key={member.user_id}>
            <button
              type="button"
              onClick={() => onSelectMember?.(member.user_id)}
              className="group flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-[var(--button-hover)]/40"
            >
              <span className="w-4 shrink-0 text-[11px] font-bold tabular-nums text-[var(--text-muted)]">{index + 1}.</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{member.user_name}</p>
                <p className="text-[10px] text-[var(--text-secondary)]">
                  {member.occupationPct}% ocupação
                  {member.tasks_overdue > 0
                    ? ` · ${member.tasks_overdue} tarefa${member.tasks_overdue === 1 ? '' : 's'} atrasada${member.tasks_overdue === 1 ? '' : 's'}`
                    : ''}
                  {member.sla_violated_pct > 0 ? ` · SLA violado ${member.sla_violated_pct.toFixed(0)}%` : ''}
                </p>
                <p className="mt-1 flex flex-wrap gap-1">
                  {member.badges.map((badge) => (
                    <span key={badge} className={`rounded-sm px-1.5 py-0.5 text-[8px] font-bold tracking-wide ${badgeClass(badge)}`}>
                      {badge}
                    </span>
                  ))}
                </p>
              </div>
              <span className={`shrink-0 text-[14px] font-bold tabular-nums ${
                getOccupancyTier(member.occupationPct) === 'critical'
                  ? 'text-red-500'
                  : getOccupancyTier(member.occupationPct) === 'high'
                    ? 'text-amber-600'
                    : 'text-[var(--text-primary)]'
              }`}>
                {member.occupationPct}%
              </span>
              <ArrowRight size={12} className="shrink-0 text-[var(--text-muted)] opacity-0 group-hover:opacity-100" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OpsOperatorsAtRiskBlock;
